import { PhoneArrowDownLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { IncomingCall, IncomingCallContact } from 'app/types/incoming-call'

/**
 * Covers the ring and the first minute of the call. Much longer than the text
 * card's 8s because a call is live while the card is up.
 */
export const CALL_ALERT_DURATION_MS = 60_000

const TICK_MS = 250

function initialsFor(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2)
  return parts
    .map((part) => part[0]!.toUpperCase())
    .join(``)
    .slice(0, 2)
}

/**
 * Sibling of `MessageAlertCard` for a phone that is ringing right now. It decays
 * like the text card, but opening the caller pins it so the person answering can
 * come back to it; from then on only the X removes it.
 *
 * Decay runs in a background tab too, or a return would find hours-old cards each claiming a phone is
 * ringing now. Only hovering holds a card, since that is someone reading it.
 */
export function IncomingCallCard({
  call,
  contacts,
  locationName,
  shownAt,
  pinned,
  isExiting,
  onOpen,
  onDismiss,
}: {
  call: IncomingCall
  /** Everyone holding the ringing number. Undefined while the lookup is out. */
  contacts?: IncomingCallContact[]
  locationName?: string
  /** Epoch ms the card first appeared, so a remount after a page load keeps its place in the countdown. */
  shownAt: number
  pinned: boolean
  isExiting: boolean
  /** Absent while the phone lookup is out: until it answers, a click could not tell open from create. */
  onOpen?: () => void
  onDismiss: () => void
}): ReactElement {
  const [entered, setEntered] = useState(false)
  const [hovered, setHovered] = useState(false)
  const countdownRef = useRef<HTMLDivElement>(null)
  const pausedRef = useRef(false)
  const onDismissRef = useRef(onDismiss)

  useEffect(() => {
    pausedRef.current = hovered
  }, [hovered])

  useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  // Before paint, so a card put back after a page load never flashes a full bar.
  useLayoutEffect(() => {
    if (countdownRef.current) countdownRef.current.style.animationDelay = `-${Math.max(Date.now() - shownAt, 0)}ms`
  }, [shownAt])

  useEffect(() => {
    if (pinned) return
    // Against the clock, not a count of ticks: a background tab can throttle ticks to one a minute.
    let deadline = shownAt + CALL_ALERT_DURATION_MS
    let lastTick = Date.now()
    const intervalId = setInterval(() => {
      const now = Date.now()
      if (pausedRef.current) deadline += now - lastTick
      lastTick = now
      if (now < deadline) return
      clearInterval(intervalId)
      onDismissRef.current()
    }, TICK_MS)
    return () => clearInterval(intervalId)
  }, [pinned, shownAt])

  const extraMatches = Math.max((contacts?.length ?? 0) - 1, 0)
  // Only a lone match identifies the caller. With several, any name, initials, or lifecycle would
  // claim an identification the card has not made, so it shows the number and defers to the dialog.
  const contact = extraMatches ? undefined : contacts?.[0]
  const contactName = contact?.preferredName || contact?.name || ``
  const formattedPhone = formatPhoneNumber(call.callerPhone) ?? call.callerPhone
  const displayName = contactName || formattedPhone || `Unknown number`
  const initials = contactName ? initialsFor(contactName) : ``
  const lifecycle = contact?.lifecycleStage
  const isPatient = lifecycle === `Patient`
  const since = formatDate({
    value: isPatient ? contact?.becamePatientDate : contact?.becameLeadDate,
    dateOnly: true,
  })

  const body = (
    <>
      {initials && (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-400 text-label-xs font-semibold text-on-light-fill">
          {initials}
        </span>
      )}

      <span className="flex min-w-0 flex-1 flex-col gap-y-0.5">
        <span className="flex min-w-0 items-center gap-x-1.5">
          <span className="min-w-0 truncate text-heading-xs font-semibold text-inverse">{displayName}</span>
          {contacts?.length === 0 && (
            <span className="shrink-0 rounded bg-primary-400/20 px-1.5 py-0.5 text-label-xs font-medium text-primary-200">
              New number
            </span>
          )}
          {lifecycle && (
            <span className="shrink-0 rounded-full bg-surface/10 px-1.5 py-0.5 text-label-xs font-medium text-inverse">
              {lifecycle}
            </span>
          )}
        </span>
        {locationName && <span className="truncate text-label-xs text-inverse/50">{locationName}</span>}
        {since && (
          <span className="truncate text-body-xs text-inverse">
            {isPatient ? `Patient` : `Lead`} since {since}
          </span>
        )}
        {extraMatches > 0 && (
          <span className="mt-0.5 text-label-xs text-primary-200">
            {extraMatches + 1} potential callers · click to view
          </span>
        )}
        {onOpen && contacts?.length === 0 && (
          <span className="mt-0.5 text-label-xs text-primary-200">Click to create a contact</span>
        )}
      </span>
    </>
  )

  const bodyClass = `flex w-full items-start gap-x-3 p-3 pr-8 pt-8 text-left`

  return (
    <div
      className={classNames(
        `overflow-hidden transition-all duration-200 ease-out`,
        isExiting ? `w-0 opacity-0` : `w-80 opacity-100`,
      )}
    >
      <div
        className={classNames(
          // Same always-dark card as MessageAlertCard: raised surface in dark, light roles inside.
          `relative w-80 overflow-hidden rounded-lg bg-inverse shadow-2xl ring-1 ring-strong transition-all duration-200 ease-out dark:bg-surface-raised`,
          entered && !isExiting ? `translate-y-0 opacity-100` : `translate-y-2 opacity-0`,
        )}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="theme-light">
          {!pinned && (
            <div className="absolute inset-x-0 top-0 h-1 bg-surface/10">
              <div
                ref={countdownRef}
                className="animate-alert-countdown h-full bg-primary-400"
                style={{
                  animationDuration: `${CALL_ALERT_DURATION_MS}ms`,
                  animationPlayState: hovered ? `paused` : `running`,
                }}
              />
            </div>
          )}
          <div className="absolute inset-x-0 top-1 flex items-center gap-x-1.5 px-3 pt-2 text-label-xs font-medium text-primary-200">
            <PhoneArrowDownLeftIcon className="h-3.5 w-3.5 animate-pulse" strokeWidth={2} />
            {pinned ? `On the line` : `Incoming call`}
          </div>

          {onOpen ? (
            <Button
              variant="ghost"
              onClick={onOpen}
              className={classNames(
                bodyClass,
                `h-auto justify-start whitespace-normal rounded-none hover:bg-surface/5`,
              )}
            >
              {body}
            </Button>
          ) : (
            <div className={bodyClass}>{body}</div>
          )}

          <Button
            variant="icon"
            size="icon-sm"
            onClick={onDismiss}
            aria-label="Dismiss incoming call"
            className="absolute right-1.5 top-2.5 text-inverse/40 hover:bg-surface/10 hover:text-inverse"
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
